import { derived, type Readable, type Writable } from 'svelte/store';
import { readable, writable } from 'svelte/store';
import type { ToastContainerType, ToastData } from '$lib/types/toast.js';
import { browser } from '$app/environment';
import { DateTime } from 'luxon';
import { getContext, hasContext, setContext } from 'svelte';
import { page } from '$app/stores';
import type { Filter } from '$lib/util/searchUtil';

/**
 * Stores automatically shared with child components using svelte context.
 * Solves Sveltekits SSR store problems/vulnerabilities.
 *
 * @see https://dev.to/jdgamble555/the-correct-way-to-use-stores-in-sveltekit-3h6i
 * @param name
 * @param fn
 * @param defaultValue
 */
export const useSharedStore = <ValueType, StoreType extends Readable<ValueType> = Readable<ValueType>>(
    name: string,
    fn: (value?: ValueType) => StoreType,
    defaultValue?: ValueType
): StoreType => {
    if (hasContext(name)) {
        return getContext<StoreType>(name);
    }

    const store = fn(defaultValue);
    setContext(name, store);

    return store;
};

export const useWritable = <T>(name: string, value: T) => useSharedStore(name, writable, value);
export const useReadable = <T>(name: string, value: T) => useSharedStore(name, readable, value);

export const toast = writable<ToastContainerType>(undefined);
export const visibleToasts = writable<ToastData[]>([]);
export const toastIdCounter = writable(0);

export const time = readable(DateTime.now(), function start(set) {
    const interval = setInterval(() => {
        set(DateTime.now());
    }, 1000);

    return function stop() {
        clearInterval(interval);
    };
});
export const slowTime = readable(DateTime.now(), function start(set) {
    const interval = setInterval(() => {
        set(DateTime.now());
    }, 60000);

    return function stop() {
        clearInterval(interval);
    };
});

export const isMobile = readable(false, function start(set) {
    if (!browser) {
        // TODO: Guess from user agent for pre-rendering?

        return;
    }

    const mediaQuery = window.matchMedia('(max-width: 1024px)');
    set(mediaQuery.matches);

    const listener = (mq: MediaQueryListEvent) => set(mq.matches);
    mediaQuery.addEventListener('change', listener);

    return () => mediaQuery.removeEventListener('change', listener);
});

export const filterURL: Writable<string | undefined> = writable(undefined);

export const searchFilter: Readable<Filter> = derived(page, ($page) => {
    let filterRaw: string | null = null;
    if (browser) {
        filterRaw = $page.url.searchParams.get('filter');
    }

    return filterRaw ? JSON.parse(decodeURIComponent(filterRaw?.replace(/\+/g, ' ') ?? '')) : {};
});

export const previousRoute: Writable<string | undefined> = writable(undefined);

export const distinctId: Writable<string | undefined> = writable(undefined);

export const mockCheckout: Writable<boolean> = writable(false);
